import React from "react";
import { shiftDate } from "./useDaySelection";

/**
 * Steps the plant-local day a panel reports on.
 *
 * Every bound comes off the response — which date is shown, which is the plant's today, and how far
 * back the picker may reach. None of it is derived here on purpose: the browser's clock can be in
 * another zone, or simply wrong, and a picker that decided for itself what "today" was would offer
 * a day the backend then refuses. Until the first response arrives there is nothing to step through,
 * so the arrows are inert rather than guessing.
 *
 * `meta` is the day block every dock panel publishes: { date, dateLabel, todayDate, minDate,
 * maxDate, isToday }.
 */
export default function DayPicker({ meta, requestedDate, onChange, label = "Day" }) {
	const current = meta?.date || null;
	const canStepBack = Boolean(current && meta?.minDate && current > meta.minDate);
	const canStepForward = Boolean(current && meta?.maxDate && current < meta.maxDate);
	// A rejected date has no metadata. Keep a way to clear the URL even in that state.
	const canReset = Boolean(requestedDate) || meta?.isToday === false;

	// Stepping onto the plant's today clears the parameter instead of naming the date, so the link
	// keeps meaning "today" tomorrow. useDaySelection owns that rule; this is the one place that
	// can produce today by stepping rather than by pressing Today.
	const step = (days) => {
		const next = shiftDate(current, days);
		onChange(next === meta?.todayDate ? null : next);
	};

	return (
		<div className="dock-day-picker" role="group" aria-label={label}>
			<button
				type="button"
				className="dock-chip dock-day-step"
				onClick={() => step(-1)}
				disabled={!canStepBack}
				aria-label="Previous day"
			>
				←
			</button>
			<span className="dock-day-current">{meta?.dateLabel || requestedDate || "…"}</span>
			<button
				type="button"
				className="dock-chip dock-day-step"
				onClick={() => step(1)}
				disabled={!canStepForward}
				aria-label="Next day"
			>
				→
			</button>
			{canReset && (
				<button type="button" className="dock-chip" onClick={() => onChange(null)}>
					Today
				</button>
			)}
		</div>
	);
}
